import {
    ChatBubbleLeftIcon,
    EnvelopeIcon,
    GlobeAltIcon,
    MapPinIcon,
    PhoneArrowUpRightIcon,
} from "@heroicons/react/24/outline";
import { ActivityParentEntityId, toPhoneNumberE164 } from "@sp-crm/core";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { MessageCompose } from "components/messages/message-compose";
import { Setting } from "components/setting";
import {
    activityRecordTypeForEntityKind,
    useDefaultActivityTypeId,
} from "components/tasks/activity-type-options";
import { phoneAction } from "components/telecom/phone-action";
import { PhoneCall } from "components/telecom/phone-call";
import { SendSmsPanel } from "components/telecom/send-sms-panel";
import { ClampedText } from "components/ui/clamped-text";
import { defaultLinkStyle } from "components/ui/link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import {
    RoundActionButton,
    RoundActionExternalLink,
    RoundActionLink,
} from "components/ui/round-action-button";
import { AdvancedSearchEntityType } from "generated/graphql";
import { computeDirectionsUrl } from "helpers/address";
import React, { useCallback, useState } from "react";
import { useResponsiveMode } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { ParsedPhoneNumber, parsePhone } from "util/phone";
import { displayableUri, phoneUri, sanitizeUri } from "util/uri";

export interface LabeledPhone {
    label: string | null;
    value: string;
}

export interface EntityOrganization {
    name: string;
    href: string;
    address: string | null;
    phone: string | null;
    website: string | null;
    email: string | null;
}

export interface EntityDetails {
    classification: string | null;
    classificationLabel: string;
    phones: LabeledPhone[];
    email: string | null;
    emailOptOut: boolean;
    address: string | null;
    website: string | null;
    organization: EntityOrganization | null;
}

const rowClasses = "flex min-w-0 max-w-full items-center gap-2";
const valueLinkClasses = `min-w-0 truncate ${defaultLinkStyle}`;
const valueTextClasses = "min-w-0 truncate text-gray-900";
const mutedClasses = "flex-shrink-0 text-xs text-gray-500";

interface PhoneLabelProps {
    label: string | null;
}

const PhoneLabel: React.FC<PhoneLabelProps> = props => {
    const { label } = props;

    if (!label) {
        return null;
    }

    return <span className={mutedClasses}>{label}</span>;
};

interface TelecomPhoneProps {
    value: string;
    phone: ParsedPhoneNumber;
    label: string | null;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

const TelecomPhone: React.FC<TelecomPhoneProps> = props => {
    const { value, phone, label, entityId, entityType } = props;

    const recordType = activityRecordTypeForEntityKind(entityType);
    const [callOpen, setCallOpen] = useState(false);
    const [smsOpen, setSmsOpen] = useState(false);
    const refreshAgenda = useAgendaRefresh();
    const defaultCallTypeId = useDefaultActivityTypeId("call", recordType);
    const defaultSmsTypeId = useDefaultActivityTypeId("sms", recordType);

    const openCall = useCallback(() => setCallOpen(true), []);
    const dismissCall = useCallback(() => {
        setCallOpen(false);
        refreshAgenda();
    }, [refreshAgenda]);
    const openSms = useCallback(() => setSmsOpen(true), []);
    const dismissSms = useCallback(() => {
        setSmsOpen(false);
        refreshAgenda();
    }, [refreshAgenda]);

    return (
        <>
            <PhoneLabel label={label} />
            <span className={valueTextClasses}>{phone.display}</span>
            <RoundActionButton
                title={`Call ${phone.display}`}
                Icon={PhoneArrowUpRightIcon}
                onClick={openCall}
            />
            <RoundActionButton
                title={`Text ${phone.display}`}
                Icon={ChatBubbleLeftIcon}
                onClick={openSms}
            />
            <Panel
                isOpen={callOpen}
                onDismiss={dismissCall}
                type={PanelType.medium}
                headerText="Phone Call">
                <PhoneCall
                    phoneNumber={value}
                    entityId={entityId}
                    activityTypeId={defaultCallTypeId}
                />
            </Panel>
            <SendSmsPanel
                isOpen={smsOpen}
                onDismiss={dismissSms}
                recipient={toPhoneNumberE164(value)}
                activityTypeId={defaultSmsTypeId}
                entityId={entityId}
            />
        </>
    );
};

interface DevicePhoneProps {
    phone: ParsedPhoneNumber;
    label: string | null;
    showActions: boolean;
}

const DevicePhone: React.FC<DevicePhoneProps> = props => {
    const { phone, label, showActions } = props;

    return (
        <>
            <PhoneLabel label={label} />
            <span className={valueTextClasses}>{phone.display}</span>
            {showActions ? (
                <>
                    <RoundActionLink
                        title={`Call ${phone.display}`}
                        Icon={PhoneArrowUpRightIcon}
                        href={phoneUri(phone.digits)}
                    />
                    <RoundActionLink
                        title={`Text ${phone.display}`}
                        Icon={ChatBubbleLeftIcon}
                        href={`sms:${phone.digits}`}
                    />
                </>
            ) : null}
        </>
    );
};

interface PhoneDetailProps {
    phone: LabeledPhone;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

export const PhoneDetail: React.FC<PhoneDetailProps> = props => {
    const { phone, entityId, entityType } = props;

    const phoneLines = usePhoneLines();
    const responsiveMode = useResponsiveMode();
    const parsed = parsePhone(phone.value);
    const action = phoneAction(phone.value, phoneLines.length, responsiveMode);

    return (
        <div className={rowClasses}>
            {action === "telecom" ? (
                <TelecomPhone
                    value={phone.value}
                    phone={parsed}
                    label={phone.label}
                    entityId={entityId}
                    entityType={entityType}
                />
            ) : (
                <DevicePhone
                    phone={parsed}
                    label={phone.label}
                    showActions={action === "device"}
                />
            )}
        </div>
    );
};

interface EmailDetailProps {
    value: string;
    optOut: boolean;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

export const EmailDetail: React.FC<EmailDetailProps> = props => {
    const { value, optOut, entityId, entityType } = props;

    const [composing, setComposing] = useState(false);
    const refreshAgenda = useAgendaRefresh();
    const openCompose = useCallback(() => setComposing(true), []);
    const openComposeFromLink = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setComposing(true);
    }, []);
    const dismissCompose = useCallback(() => {
        setComposing(false);
        refreshAgenda();
    }, [refreshAgenda]);

    return (
        <div className={rowClasses}>
            <a
                href="#"
                title={`Email ${value}`}
                onClick={openComposeFromLink}
                className={valueLinkClasses}>
                {value}
            </a>
            {optOut ? (
                <Setting name="emailOptOut">
                    <span className={mutedClasses} title="Opted out of bulk email">
                        no bulk email
                    </span>
                </Setting>
            ) : null}
            <RoundActionButton
                title={`Email ${value}`}
                Icon={EnvelopeIcon}
                onClick={openCompose}
            />
            {composing ? (
                <MessageCompose
                    entityId={entityId}
                    entityType={entityType}
                    initialRecipients={[value]}
                    onDismiss={dismissCompose}
                />
            ) : null}
        </div>
    );
};

export const AddressDetail: React.FC<{ value: string }> = props => {
    const { value } = props;

    return (
        <div className={rowClasses}>
            <span className="min-w-0 truncate text-gray-700">{value}</span>
            <RoundActionExternalLink
                title="Get directions"
                Icon={MapPinIcon}
                href={computeDirectionsUrl(value, "")}
            />
        </div>
    );
};

export const WebsiteDetail: React.FC<{ value: string }> = props => {
    const { value } = props;

    return (
        <div className={rowClasses}>
            <a
                href={sanitizeUri(value)}
                target="_blank"
                rel="noreferrer"
                title="Open website"
                className={valueLinkClasses}>
                {displayableUri(value, 48)}
            </a>
            <RoundActionExternalLink
                title="Open website"
                Icon={GlobeAltIcon}
                href={sanitizeUri(value)}
            />
        </div>
    );
};

export const EntitySummary: React.FC<{ summary: string | null }> = props => {
    const { summary } = props;

    if (!summary) {
        return null;
    }

    return (
        <ClampedText lines={3}>
            <p className="whitespace-pre-wrap text-sm italic text-gray-500">{summary}</p>
        </ClampedText>
    );
};

interface EntityContactDetailsProps {
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
    details: EntityDetails;
}

export const EntityContactDetails: React.FC<EntityContactDetailsProps> = props => {
    const { entityId, entityType, details } = props;
    const { classification, phones, email, emailOptOut, address, website, organization } =
        details;

    const hasDetails =
        classification ||
        phones.length > 0 ||
        email ||
        address ||
        website ||
        organization;

    if (!hasDetails) {
        return null;
    }

    return (
        <div className="flex flex-col items-start gap-x-5 gap-y-1.5 text-sm sm:flex-row sm:flex-wrap sm:items-center">
            {phones.map(phone => (
                <PhoneDetail
                    key={phone.value}
                    phone={phone}
                    entityId={entityId}
                    entityType={entityType}
                />
            ))}
            {email ? (
                <EmailDetail
                    value={email}
                    optOut={emailOptOut}
                    entityId={entityId}
                    entityType={entityType}
                />
            ) : null}
            {address ? <AddressDetail value={address} /> : null}
            {website ? <WebsiteDetail value={website} /> : null}
        </div>
    );
};
